/**
 * The mobile layout library — `src/mobile/`.
 *
 * Layer 3 of the three the design keeps apart (docs/MOBILE-REDESIGN.md §4.0):
 *
 *   1. **Kit primitive** (`@parsina/framework`) — design language shared by
 *      both surfaces: `Button`, `Card`, `Badge`, `Alert`, `EmptyState`,
 *      `ErrorState`, `ConfirmDialog`, `StatusBadge`, the form controls. Reach
 *      here FIRST; hand-rolling something the kit already has is a defect.
 *   2. **App-shared, surface-agnostic** (`src/hooks/`, `src/lib/`,
 *      `src/constants/`, `*.shared.js`) — logic, vocabulary, view-state,
 *      formatting. Imported by BOTH surfaces, which is what stops the two
 *      disagreeing about a count or a status.
 *   3. **Mobile layout** (here) — anatomy, chrome, cards, sheets. Imported
 *      only by `*.mobile.jsx`, and never exported back into a `.web.jsx`.
 *
 * The test for something new: does it carry LAYOUT opinion? Layer 3. BEHAVIOUR
 * or VOCABULARY? Layer 2, and both surfaces use it. DESIGN LANGUAGE with no
 * layout? It belongs in the framework — open a PR there rather than copying it
 * in here.
 *
 * Several components here are thin wrappers over a kit primitive, adding a
 * capability the pinned v0.10.1 lacks (`MobileSheet`'s keyboard inset and
 * history entry, `MobileHeader`'s search field). They are wrappers rather than
 * forks precisely so that raising the pin deletes them instead of leaving two
 * divergent implementations behind.
 */

// Shell
export { default as MobileScreen } from './MobileScreen';
export { default as MobileHeader } from './MobileHeader';
export { default as MobileBottomNav } from './MobileBottomNav';
export { default as MobileMoreSheet } from './MobileMoreSheet';

// Overlays
export { default as MobileSheet } from './MobileSheet';
export { default as MobileFormSheet, MobileField } from './MobileFormSheet';
export { default as MobileRowMenu } from './MobileRowMenu';
export {
  default as useOverlayHistory,
  overlayOwnsPop,
  consumeOverlayEntry,
} from './useOverlayHistory';

// Content
export { default as MobileList } from './MobileList';
export { default as MobileCardSkeleton } from './MobileCardSkeleton';
export {
  default as MobileCard,
  MobileCardHead,
  MobileCardMeta,
  MobileCardActions,
} from './MobileCard';
export { default as MobileFilterBar } from './MobileFilterBar';
export { default as MobileSectionNav } from './MobileSectionNav';
export { default as MobileStatTiles } from './MobileStatTiles';
export { default as MobileMoneyRow } from './MobileMoneyRow';
export { MobileMoneyTile, MobileDetailRow } from './MobileFacts';
export { default as MobileDateBar } from './MobileDateBar';
export { default as MobileDesktopNote } from './MobileDesktopNote';

// Domain cards (§4.7) — one per object, so a patient looks the same wherever a
// patient appears. Each is render-only: it takes its facts already derived and
// its words already translated.
export { default as MobileAppointmentCard } from './cards/MobileAppointmentCard';

// Domain cards (§4.7) — one per object, so a record looks the same wherever it
// appears. Render-only: data in via props, events out via callbacks.
export { default as MobilePaymentCard } from './cards/MobilePaymentCard';
export { default as MobileClaimCard } from './cards/MobileClaimCard';
export { default as MobileDistributionList } from './cards/MobileDistributionList';

// Domain cards — one per object, so an invoice looks the same wherever an
// invoice appears. Render-only: a card that needs a computed field takes it
// already computed (§4.7).
export { default as MobileInvoiceCard } from './cards/MobileInvoiceCard';

// Task sheets that are big enough to own a file, and are opened from more than
// one screen (the settle sheet is reached from the cash desk AND from reception).
export { default as MobileSettleSheet } from './sheets/MobileSettleSheet';
